import React, { useState } from "react";
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";
import SearchableMedicineDropdown from "../../components/SearchableMedicineDropdown";
import {
    Plus,
    Trash2,
    Calendar,
    Save,
    AlertCircle,
    Package,
    RotateCcw
} from "lucide-react";
import { Link } from "react-router-dom";

const NewOpeningStock = () => {
    const [currentDate] = useState(new Date().toISOString().split("T")[0]);
    const [medicines, setMedicines] = useState([
        {
            id: Date.now(),
            medicine_id: "",
            medicineName: "",
            batch_number: "",
            expiry_date: new Date(new Date().setMonth(new Date().getMonth() + 6)).toISOString().split("T")[0],
            quantity: 0,
            free_quantity: 0,
            purchase_price: "",
            mrp: "",
            selling_price: "",
            gst_percentage: 0,
            total: 0
        },
    ]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("success");
    const [showAlert, setShowAlert] = useState(false);
    // const navigate = useNavigate();
    const [focusedRowId, setFocusedRowId] = useState(null);

    const showAlertMessage = (msg, type) => {
        setAlertMessage(msg);
        setAlertType(type);
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
    };

    const handleEnterKey = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const focusable = document.querySelectorAll('input, select, textarea, button, [tabindex="0"]');
            const list = Array.from(focusable).filter(el => !el.disabled && el.tabIndex !== -1);
            const index = list.indexOf(e.target);
            if (index > -1 && index < list.length - 1) {
                list[index + 1].focus();
            }
        }
    };

    const addMedicine = () => {
        const newId = Date.now();
        setMedicines(prev => [
            ...prev,
            {
                id: newId,
                medicine_id: "",
                medicineName: "",
                batch_number: "",
                expiry_date: new Date(new Date().setMonth(new Date().getMonth() + 6)).toISOString().split("T")[0],
                quantity: 0,
                free_quantity: 0,
                purchase_price: "",
                mrp: "",
                selling_price: "",
                gst_percentage: 0,
                total: 0
            },
        ]);
        setFocusedRowId(newId);
    };

    const removeMedicine = (id) => {
        if (medicines.length > 1) {
            setMedicines(medicines.filter(m => m.id !== id));
        }
    };

    const handleMedicineSelect = (index, selectedMedicine) => {
        const updated = [...medicines];
        const batchDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');

        if (selectedMedicine) {
            updated[index].medicine_id = selectedMedicine.id;
            updated[index].medicineName = selectedMedicine.name;
            updated[index].gst_percentage = selectedMedicine.gst_percentage || 0;
            updated[index].batch_number = `OPEN${batchDate}${String(index + 1).padStart(2, '0')}`;

            // Auto fill price if known (optional, often opening stock has varied costs)
            // updated[index].purchase_price = selectedMedicine.purchase_price || ""; 
        } else {
            updated[index].medicine_id = "";
            updated[index].medicineName = "";
            updated[index].batch_number = "";
        }
        setMedicines(updated);

        if (selectedMedicine) {
            setTimeout(() => {
                const batchInput = document.getElementById(`batch-${index}`);
                if (batchInput) {
                    batchInput.focus();
                    batchInput.select();
                }
            }, 100);
        }
    };

    const handleChange = (index, field, value) => {
        const updated = [...medicines];
        updated[index][field] = value;

        // Auto-calculate total
        if (field === 'quantity' || field === 'purchase_price') {
            const qty = parseFloat(updated[index].quantity) || 0;
            const price = parseFloat(updated[index].purchase_price) || 0;
            updated[index].total = (qty * price).toFixed(2);
        }

        setMedicines(updated);
    };

    const handleLastFieldEnter = (e, index) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (index === medicines.length - 1) {
                addMedicine();
            } else {
                setFocusedRowId(medicines[index + 1].id);
            }
        }
    };

    const validateForm = () => {
        for (let i = 0; i < medicines.length; i++) {
            const m = medicines[i];
            if (!m.medicine_id) {
                showAlertMessage(`Row ${i + 1}: Please select a medicine`, "error");
                return false;
            }
            if (!m.batch_number) {
                showAlertMessage(`Row ${i + 1}: Batch Number is required`, "error");
                return false;
            }
            if (!m.quantity || parseFloat(m.quantity) <= 0) {
                showAlertMessage(`Row ${i + 1}: Valid Quantity is required`, "error");
                return false;
            }
            if (!m.purchase_price || parseFloat(m.purchase_price) <= 0) {
                showAlertMessage(`Row ${i + 1}: Purchase Price is required`, "error");
                return false;
            }
            if (!m.selling_price || parseFloat(m.selling_price) <= 0) {
                showAlertMessage(`Row ${i + 1}: Selling Price (MRP/Sale) is required`, "error");
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const payload = {
                date: currentDate,
                items: medicines.map(m => ({
                    medicine_id: m.medicine_id,
                    batch_number: m.batch_number,
                    expiry_date: m.expiry_date,
                    quantity: m.quantity,
                    purchase_price: m.purchase_price,
                    mrp: m.mrp || m.selling_price, // Fallback if MRP empty
                    selling_price: m.selling_price,
                    gst_percentage: m.gst_percentage
                }))
            };

            await axiosInstance.post('/opening-stock', payload);
            showAlertMessage("Opening stock added successfully!", "success");

            // Reset form
            setTimeout(() => {
                setMedicines([{
                    id: Date.now(),
                    medicine_id: "",
                    medicineName: "",
                    batch_number: "",
                    expiry_date: new Date(new Date().setMonth(new Date().getMonth() + 6)).toISOString().split("T")[0],
                    quantity: 0,
                    free_quantity: 0,
                    purchase_price: "",
                    mrp: "",
                    selling_price: "",
                    gst_percentage: 0,
                    total: 0
                }]);
            }, 1000);

        } catch (error) {
            console.error(error);
            showAlertMessage(error.response?.data?.error || "Failed to add opening stock", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-6 px-2 sm:px-4 md:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center space-x-3">
                            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-3 rounded-xl shadow-md">
                                <Package className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Opening Stock Entry</h1>
                                <p className="text-xs sm:text-sm text-gray-600 mt-1">Enter initial stock for existing inventory</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <Link to="/purchase?type=OPENING_STOCK">
                                <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors shadow-sm text-sm">
                                    <RotateCcw size={16} />
                                    <span>View History</span>
                                </button>
                            </Link>
                            <div className="flex items-center gap-2 text-sm text-gray-600 bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200">
                                <Calendar className="w-4 h-4 text-indigo-500" />
                                <span>Date: <strong>{currentDate}</strong></span>
                            </div>
                        </div>
                    </div>

                    {showAlert && (
                        <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 shadow-sm ${alertType === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"
                            }`}>
                            <AlertCircle className="h-5 w-5" />
                            <span className="font-medium">{alertMessage}</span>
                        </div>
                    )}

                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
                        {/* Medicines List */}
                        <div className="space-y-4 mb-8">
                            {medicines.map((m, i) => (
                                <div key={m.id} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-all duration-300 relative group">
                                    <div className="absolute -left-3 top-6 bg-indigo-100 text-indigo-700 font-bold w-8 h-8 rounded-full flex items-center justify-center text-sm border-2 border-white shadow-sm">
                                        {i + 1}
                                    </div>

                                    {/* Row 1: Medicine & Batch */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 ml-2">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Medicine Name</label>
                                            <SearchableMedicineDropdown
                                                placeholder="Search medicine..."
                                                value={m.medicine_id}
                                                onChange={med => handleMedicineSelect(i, med)}
                                                onKeyDown={handleEnterKey}
                                                autoFocus={m.id === focusedRowId}
                                                id={`medicine-dropdown-${i}`}
                                            />
                                        </div>
                                        <div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Batch Number</label>
                                                    <input
                                                        id={`batch-${i}`}
                                                        value={m.batch_number}
                                                        onChange={e => handleChange(i, "batch_number", e.target.value)}
                                                        placeholder="Batch No."
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Expiry Date</label>
                                                    <input
                                                        type="date"
                                                        value={m.expiry_date}
                                                        onChange={e => handleChange(i, "expiry_date", e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Row 2: Qty & Pricing */}
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 ml-2 items-end">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">Quantity</label>
                                            <input
                                                type="number"
                                                value={m.quantity}
                                                onChange={e => handleChange(i, "quantity", e.target.value)}
                                                className="w-full px-3 py-2 border border-blue-200 bg-blue-50 text-blue-800 rounded-lg font-bold focus:ring-2 focus:ring-blue-500"
                                                placeholder="Qty"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">Purchase Price</label>
                                            <input
                                                type="number"
                                                value={m.purchase_price}
                                                onChange={e => handleChange(i, "purchase_price", e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                                placeholder="Cost"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">MRP</label>
                                            <input
                                                type="number"
                                                value={m.mrp}
                                                onChange={e => handleChange(i, "mrp", e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                                placeholder="MRP"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">Sale Price</label>
                                            <input
                                                type="number"
                                                value={m.selling_price}
                                                onChange={e => handleChange(i, "selling_price", e.target.value)}
                                                onKeyDown={(e) => handleLastFieldEnter(e, i)}
                                                className="w-full px-3 py-2 border border-green-200 bg-green-50 text-green-800 rounded-lg font-bold focus:ring-2 focus:ring-green-500"
                                                placeholder="Sale Price"
                                            />
                                        </div>
                                        <div className="flex justify-end pb-1">
                                            {medicines.length > 1 && (
                                                <button onClick={() => removeMedicine(m.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                                                    <Trash2 size={18} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center pt-6 border-t border-gray-100">
                            <button
                                onClick={addMedicine}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-dashed border-indigo-300 text-indigo-600 rounded-xl hover:bg-indigo-50 transition-all font-semibold"
                            >
                                <Plus size={20} /> Add Another Item
                            </button>

                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className={`w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all font-bold text-lg ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {isSubmitting ? (
                                    <>Processing...</>
                                ) : (
                                    <>
                                        <Save size={20} /> Save Opening Stock
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Authenticated>
    );
};

export default NewOpeningStock;
